import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Clock, User, MapPin, RefreshCw, Calendar, Building2, ArrowRight } from 'lucide-react';
import { 
  getInterventionsEnCours,
  getUserById,
  getCentreById,
  getTypeInterventionById,
  centres 
} from '../../data/mockData';

function TableauDeBord({ user }) {
  const navigate = useNavigate();
  const [interventionsEnCours, setInterventionsEnCours] = useState([]);
  const [selectedIntervention, setSelectedIntervention] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  
  // États pour l'accès rapide
  const [quickAccessStep, setQuickAccessStep] = useState(1);
  const [selectedCentre, setSelectedCentre] = useState('');
  const [selectedDate, setSelectedDate] = useState('');

  const loadInterventions = () => {
    const interventions = getInterventionsEnCours();
    setInterventionsEnCours(interventions);
    setLastUpdate(new Date());
  };

  useEffect(() => {
    loadInterventions();

    // Actualisation automatique toutes les 2 minutes (120000 ms)
    const interval = setInterval(() => {
      loadInterventions();
    }, 120000);

    return () => clearInterval(interval);
  }, []);

  const calculerDureeEnCours = (intervention) => {
    if (!intervention.heureDebut) return '-';
    
    const [h, m] = intervention.heureDebut.split(':').map(Number);
    const now = new Date();
    const totalMinutes = (now.getHours() * 60 + now.getMinutes()) - (h * 60 + m);
    
    const heures = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    return `${heures}h${minutes.toString().padStart(2, '0')}`;
  };

  const handleRefresh = () => {
    loadInterventions();
  };

  const handleViewDetails = (intervention) => {
    setSelectedIntervention(intervention);
  };

  const closeModal = () => {
    setSelectedIntervention(null);
  };

  const handleQuickAccessCentre = (centreId) => {
    setSelectedCentre(centreId);
    setQuickAccessStep(2);
  };

  const handleQuickAccessDate = (date) => {
    setSelectedDate(date);
    setQuickAccessStep(3);
  };

  const handleQuickAccessGo = () => {
    // Rediriger vers l'agenda global filtré
    navigate(`/agenda-global?centre=${selectedCentre}&date=${selectedDate}`);
  };

  const resetQuickAccess = () => {
    setQuickAccessStep(1);
    setSelectedCentre('');
    setSelectedDate('');
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Tableau de bord</h1>
          <p className="text-muted">Interventions en cours - Temps réel</p>
        </div>
        <button onClick={handleRefresh} className="btn btn-secondary">
          <RefreshCw size={20} />
          Actualiser
        </button>
      </div>

      <div className="dashboard-info">
        <div className="info-box info-primary">
          <Clock size={20} />
          <p>
            Dernière actualisation : {lastUpdate.toLocaleTimeString('fr-FR')}
            <br />
            <small>Actualisation automatique toutes les 2 minutes</small>
          </p>
        </div>
      </div>

      <div className="dashboard-row">

        {/* Accès rapide en 3 étapes */}
        <div className="quick-access-card">
          <div className="quick-access-header">
            <Calendar size={24} />
            <h3>Accès rapide</h3>
          </div>
          
          <div className="quick-access-steps">
            <div className={`quick-access-step ${quickAccessStep >= 1 ? 'active' : ''} ${quickAccessStep > 1 ? 'completed' : ''}`}>
              <div className="step-number">1</div>
              <div className="step-content">
                <p className="step-title">Choisir un centre</p>
                {quickAccessStep === 1 && (
                  <div className="step-options">
                    {centres.filter(c => c.actif).map(centre => (
                      <button
                        key={centre.id}
                        className="step-option-btn"
                        onClick={() => handleQuickAccessCentre(centre.id)}
                      >
                        <Building2 size={16} />
                        {centre.nom}
                      </button>
                    ))}
                  </div>
                )}
                {quickAccessStep > 1 && selectedCentre && (
                  <p className="step-selected">
                    <Building2 size={14} />
                    {getCentreById(parseInt(selectedCentre))?.nom}
                    <button className="btn-reset-step" onClick={resetQuickAccess}>Modifier</button>
                  </p>
                )}
              </div>
            </div>

            <div className={`quick-access-step ${quickAccessStep >= 2 ? 'active' : ''} ${quickAccessStep > 2 ? 'completed' : ''}`}>
              <div className="step-number">2</div>
              <div className="step-content">
                <p className="step-title">Choisir une date</p>
                {quickAccessStep === 2 && (
                  <div className="step-date-input">
                    <input
                      type="date"
                      value={selectedDate}
                      onChange={(e) => handleQuickAccessDate(e.target.value)}
                      max={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                )}
                {quickAccessStep > 2 && selectedDate && (
                  <p className="step-selected">
                    <Calendar size={14} />
                    {new Date(selectedDate).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>
                )}
              </div>
            </div>

            <div className={`quick-access-step ${quickAccessStep >= 3 ? 'active' : ''}`}>
              <div className="step-number">3</div>
              <div className="step-content">
                <p className="step-title">Consulter l'agenda</p>
                {quickAccessStep === 3 && (
                  <button
                    className="btn btn-primary"
                    onClick={handleQuickAccessGo}
                  >
                    <ArrowRight size={20} />
                    Voir l'historique
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {interventionsEnCours.length === 0 ? (
        <div className="empty-state">
          <Clock size={48} className="icon-muted" />
          <p>Aucune intervention en cours actuellement</p>
        </div>
      ) : (
        <div className="interventions-en-cours-grid">
          {interventionsEnCours.map(intervention => {
            const technicien = getUserById(intervention.technicienId);
            const centre = getCentreById(intervention.centreId);
            const type = getTypeInterventionById(intervention.typeInterventionId);
            
            return (
              <div key={intervention.id} className="intervention-en-cours-card">
                <div className="intervention-card-header">
                  <div>
                    {type && (
                      <span 
                        className="type-badge" 
                        style={{ backgroundColor: type.couleur }}
                      >
                        {type.code}
                      </span>
                    )}
                    <h3>{intervention.plaque}</h3>
                  </div>
                  <span className="badge badge-warning pulse">En cours</span>
                </div>

                <div className="intervention-card-body">
                  <div className="info-row">
                    <User size={16} className="icon-muted" />
                    <div>
                      <p className="text-sm text-muted">Technicien</p>
                      <p><strong>{technicien?.prenom} {technicien?.nom}</strong></p>
                    </div>
                  </div>

                  <div className="info-row">
                    <MapPin size={16} className="icon-muted" />
                    <div>
                      <p className="text-sm text-muted">Centre</p>
                      <p>{centre?.nom}</p>
                    </div>
                  </div>

                  <div className="info-row">
                    <Clock size={16} className="icon-muted" />
                    <div>
                      <p className="text-sm text-muted">Durée écoulée</p>
                      <p className="text-primary"><strong>{calculerDureeEnCours(intervention)}</strong></p>
                    </div>
                  </div>

                  <div className="info-row">
                    <Clock size={16} className="icon-muted" />
                    <div>
                      <p className="text-sm text-muted">Démarrée à</p>
                      <p>{intervention.heureDebut || '-'}</p>
                    </div>
                  </div>
                </div>

                <div className="intervention-card-footer">
                  <button 
                    onClick={() => handleViewDetails(intervention)}
                    className="btn btn-sm btn-primary btn-block"
                  >
                    <Eye size={16} />
                    Voir les détails
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal de détails */}
      {selectedIntervention && (
        <>
          <div className="modal-overlay" onClick={closeModal}></div>
          <div className="modal">
            <div className="modal-header">
              <h2>Détails de l'intervention</h2>
              <button onClick={closeModal} className="modal-close">×</button>
            </div>
            
            <div className="modal-body">
              {(() => {
                const technicien = getUserById(selectedIntervention.technicienId);
                const centre = getCentreById(selectedIntervention.centreId);
                const type = getTypeInterventionById(selectedIntervention.typeInterventionId);
                
                return (
                  <>
                    <div className="modal-section">
                      <h3>Informations générales</h3>
                      <div className="info-grid">
                        <div className="info-item">
                          <p className="text-muted">Plaque</p>
                          <p><strong>{selectedIntervention.plaque}</strong></p>
                        </div>
                        <div className="info-item">
                          <p className="text-muted">Type</p>
                          {type && (
                            <span 
                              className="type-badge" 
                              style={{ backgroundColor: type.couleur }}
                            >
                              {type.code} - {type.libelle}
                            </span>
                          )}
                        </div>
                        <div className="info-item">
                          <p className="text-muted">Technicien</p>
                          <p>{technicien?.prenom} {technicien?.nom}</p>
                        </div>
                        <div className="info-item">
                          <p className="text-muted">Centre</p>
                          <p>{centre?.nom}</p>
                        </div>
                        <div className="info-item">
                          <p className="text-muted">Heure de début</p>
                          <p><strong>{selectedIntervention.heureDebut}</strong></p>
                        </div>
                        <div className="info-item">
                          <p className="text-muted">Durée écoulée</p>
                          <p className="text-primary">
                            <strong>{calculerDureeEnCours(selectedIntervention)}</strong>
                          </p>
                        </div>
                      </div>
                    </div>

                    {selectedIntervention.commentaire && (
                      <div className="modal-section">
                        <h3>Commentaire</h3>
                        <p>{selectedIntervention.commentaire}</p>
                      </div>
                    )}

                    {selectedIntervention.photoAvant && (
                      <div className="modal-section">
                        <h3>Photo avant intervention</h3>
                        <div className="photo-modal">
                          <img 
                            src={selectedIntervention.photoAvant} 
                            alt="Photo avant" 
                            className="modal-image"
                          />
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
            
            <div className="modal-footer">
              <button onClick={closeModal} className="btn btn-secondary">
                Fermer
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default TableauDeBord;

